import React, {useEffect} from 'react'
import {RouteComponentProps, useHistory, useLocation} from 'react-router-dom'

import {makeStyles} from 'tss-react/mui'

import {useArticleDetailsQuery} from '@progyny/generated/graphql'
import NotFoundPage from '@progyny/pages/404'
import TopicDetails from '@progyny/pages/KnowledgeCenter/Article/Details/TopicDetails'
import {useLanguageCriteria} from '@progyny/store/languageStore'
import {useRefetchContext} from '@progyny/store/refetchStore'

type ParamsProps = {
  slug: string
}

const useStyles = makeStyles()(() => ({
  topImage: {
    height: '100%',
  },
}))

type Article = {
  squareThumbnailUrl: string
  slug: string
  title: string
}

type ArticleCategory = {
  imageUrl: string
  slug: string
  name: string
}

interface ArticleDetailsProps extends RouteComponentProps<ParamsProps> {}

const ArticleDetails: React.ComponentType<ArticleDetailsProps> = ({
  match: {
    params: {slug},
  },
}: ArticleDetailsProps) => {
  const {classes} = useStyles()
  const history = useHistory()
  const {isLanguageUpdated, setIsLanguageUpdated} = useLanguageCriteria()
  const location = useLocation()
  const previewToken = new URLSearchParams(location.search)
  const token = previewToken.get('preview')
  const {registerRefetch} = useRefetchContext()
  const {data, loading, refetch} = useArticleDetailsQuery({
    variables: {
      slug,
      preview: token,
    },
  })

  // added debouncing to avoid caching and refetch after change language mutation
  useEffect(() => {
    const timeout = setTimeout(() => {
      refetch({slug, preview: token})
    }, 200)

    return () => clearTimeout(timeout)
  }, [slug, token, refetch])

  useEffect(() => {
    const refetchFn = () => {
      refetch({slug, preview: token})
    }

    const key = `articleDetails:${slug}`
    registerRefetch(key, refetchFn)
  }, [registerRefetch, refetch, slug, token])

  useEffect(() => {
    if (isLanguageUpdated) {
      refetch()
      setIsLanguageUpdated(false)
    }
  }, [isLanguageUpdated, setIsLanguageUpdated, refetch])

  if (loading) {
    return <TopicDetails loading={loading} onBackClick={history.goBack} />
  }

  if (!data!.articles) {
    return <NotFoundPage />
  }

  return (
    <TopicDetails
      loading={loading}
      isFeatured={data?.articles?.[0]?.isFeatured}
      onBackClick={history.goBack}
      title={data?.articles?.[0]?.title}
      header={
        data?.articles[0]?.headerImageUrl && (
          <img
            className={classes.topImage}
            alt={data?.articles?.[0]?.title}
            src={data?.articles[0]?.headerImageUrl}
          />
        )
      }
      html={data?.articles[0]?.text}
      relatedTopics={data?.articles?.[0]?.relatedArticles! as Article[]}
      relatedSegments={
        data?.articles?.[0]?.relatedCategories as ArticleCategory[]
      }
    />
  )
}

export default ArticleDetails
